/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { processStepFromUrl } from './use-wizard-navigation-helpers';
import {
  getCurrentStepIndex,
  hasNextStep,
  getNextStepId,
  hasPreviousStep,
  getPreviousStepId
} from './use-wizard-navigation-step-helpers';
import { getStepFromUrl } from './use-wizard-navigation-url';
import { validateAndNavigateToStep } from './use-wizard-navigation-callbacks';

export interface UseWizardNavigationOptions {
  wizardId: string;
  currentStepId: string;
  stepIdOrder: string[];
  onStepChange?: (stepId: string) => void;
  stepParamName?: string;
  disabled?: boolean;
}

export interface UseWizardNavigationReturn {
  goToStep: (stepId: string) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  isValidStep: (stepId: string) => boolean;
}

const DEFAULT_STEP_PARAM_NAME = 'step';

/**
 * Hook to manage wizard navigation with URL synchronization
 *
 * @param {UseWizardNavigationOptions} options - Configuration options
 * @returns {UseWizardNavigationReturn} Navigation functions
 */
export function useWizardNavigation(
  options: UseWizardNavigationOptions
): UseWizardNavigationReturn {
  const {
    wizardId,
    currentStepId,
    stepIdOrder,
    onStepChange,
    stepParamName = DEFAULT_STEP_PARAM_NAME,
    disabled = false,
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();

  // Get step from URL on mount and when URL changes
  useEffect(() => {
    if (disabled) return;
    const urlStep = getStepFromUrl(searchParams, stepParamName);
    processStepFromUrl(
      urlStep,
      currentStepId,
      stepIdOrder,
      stepParamName,
      setSearchParams,
      onStepChange
    );
  }, [searchParams, stepParamName, currentStepId, stepIdOrder, onStepChange, setSearchParams, disabled]);

  // Note: URL sync when currentStepId changes is handled by setCurrentStepId in steps-context.
  // This effect was causing a race condition (replace-after-push) that broke browser back.
  // The URL↔state sync for popstate (browser back) is handled by the effect above (lines 66-77).

  const isValidStep = useCallback(
    (stepId: string): boolean => {
      return stepIdOrder.includes(stepId);
    },
    [stepIdOrder]
  );

  const goToStep = useCallback(
    (stepId: string) => {
      validateAndNavigateToStep(
        stepId,
        isValidStep,
        stepIdOrder,
        setSearchParams,
        stepParamName,
        onStepChange
      );
    },
    [stepIdOrder, stepParamName, onStepChange, setSearchParams, isValidStep]
  );

  const goToNextStep = useCallback(() => {
    const currentIndex = getCurrentStepIndex(currentStepId, stepIdOrder);
    if (hasNextStep(currentIndex, stepIdOrder)) {
      goToStep(getNextStepId(currentIndex, stepIdOrder));
    }
  }, [currentStepId, stepIdOrder, goToStep]);

  const goToPreviousStep = useCallback(() => {
    const currentIndex = getCurrentStepIndex(currentStepId, stepIdOrder);
    if (hasPreviousStep(currentIndex)) {
      goToStep(getPreviousStepId(currentIndex, stepIdOrder));
    }
  }, [currentStepId, stepIdOrder, goToStep]);

  return {
    goToStep,
    goToNextStep,
    goToPreviousStep,
    isValidStep,
  };
}
